import { ApiError, createAction } from 'redux-api-middleware';
import { endpointWithUserId } from '../../common/url';
import { State as StoreState } from '../types/State';
import { HTTP_GET } from './ApiAction';
import { BonusActivity, SubmissionState } from './BonusActivity';
import { RewardType } from './BonusShop';

/* Action definition for loading both dashboards */
export const LOAD_BONUS_TIMELINE = 'barmer-bonus-shared/timeline/bonus/LOAD' as const;
export const LOAD_BONUS_TIMELINE_SUCCESS = 'barmer-bonus-shared/timeline/bonus/SUCCESS' as const;
export const LOAD_BONUS_TIMELINE_FAILURE = 'barmer-bonus-shared/timeline/bonus/FAILURE' as const;
export const RESET_BONUS_TIMELINE = 'barmer-bonus-shared/timeline/bonus/RESET' as const;

type LoadBonusTimeline = { type: typeof LOAD_BONUS_TIMELINE; payload?: ApiError; error?: boolean };
type BonusTimelineUpdate = { type: typeof LOAD_BONUS_TIMELINE_SUCCESS; payload: BonusTimeline };
type BonusTimelineUpdateFailure = {
  type: typeof LOAD_BONUS_TIMELINE_FAILURE;
  payload?: ApiError;
  error?: boolean;
};
type BonusTimelineReset = { type: typeof RESET_BONUS_TIMELINE };

export type Action =
  | LoadBonusTimeline
  | BonusTimelineUpdate
  | BonusTimelineUpdateFailure
  | BonusTimelineReset;

export const BONUS_TIMELINE_LOADING = 'loading' as const;
export const BONUS_TIMELINE_LOADED = 'loaded' as const;
export const BONUS_TIMELINE_ERROR = 'error' as const;
export const BONUS_TIMELINE_UNKNOWN = 'unknown' as const;

export type LoadState =
  | typeof BONUS_TIMELINE_LOADING
  | typeof BONUS_TIMELINE_LOADED
  | typeof BONUS_TIMELINE_ERROR
  | typeof BONUS_TIMELINE_UNKNOWN;

export type BonusTimeline = {
  rejected: Array<BonusActivity>;
  timeline: Array<BonusTimelineItem>;
};

// eslint-disable-next-line no-shadow
export enum FilterType {
  None,
  Activity,
  Reward,
}

export const BONUS_TIMELINE_PAYLOAD_ACTIVITY_TYPE = 'activity' as const;
export const BONUS_TIMELINE_PAYLOAD_MESSAGE_TYPE = 'message' as const;
export const BONUS_TIMELINE_PAYLOAD_ORDER_ITEM_TYPE = 'order' as const;
export const BONUS_TIMELINE_PAYLOAD_GIFT_ITEM_TYPE = 'gift' as const;

export type TimelinePayloadType =
  | typeof BONUS_TIMELINE_PAYLOAD_ACTIVITY_TYPE
  | typeof BONUS_TIMELINE_PAYLOAD_MESSAGE_TYPE
  | typeof BONUS_TIMELINE_PAYLOAD_ORDER_ITEM_TYPE
  | typeof BONUS_TIMELINE_PAYLOAD_GIFT_ITEM_TYPE;

export type BonusTimelineOrderItem = {
  title: string;
  subtitle: string;
  rewardType: RewardType;
  submissionDate: string;
  submissionState: SubmissionState | null | undefined;
};

type ActivityItem = {
  type: typeof BONUS_TIMELINE_PAYLOAD_ACTIVITY_TYPE;
  payload: BonusActivity;
};

export type MessagePayload = {
  messageId: string;
  icon: string;
  text: string;
};

type MessageItem = {
  type: typeof BONUS_TIMELINE_PAYLOAD_MESSAGE_TYPE;
  text: string;
  payload: MessagePayload;
};

type OrderItem = {
  type: typeof BONUS_TIMELINE_PAYLOAD_ORDER_ITEM_TYPE;
  payload: BonusTimelineOrderItem;
};

type GiftItem = {
  type: typeof BONUS_TIMELINE_PAYLOAD_GIFT_ITEM_TYPE;
  payload: BonusTimelineOrderItem;
};

export type BonusTimelineItem = ActivityItem | MessageItem | OrderItem | GiftItem;

export type State = {
  loading: LoadState;
  bonus: BonusTimeline;
  error?: ApiError;
};

const initialState: State = {
  loading: BONUS_TIMELINE_UNKNOWN,
  error: undefined,
  bonus: {
    rejected: [],
    timeline: [],
  },
};

export default function reducer(state: State = initialState, action: Action): State {
  switch (action.type) {
    case LOAD_BONUS_TIMELINE:
      return {
        ...state,
        loading: action.error === undefined ? BONUS_TIMELINE_LOADING : BONUS_TIMELINE_ERROR,
        error: action.error ? action.payload : undefined,
      };

    case LOAD_BONUS_TIMELINE_SUCCESS:
      return {
        ...state,
        loading: BONUS_TIMELINE_LOADED,
        bonus: action.payload,
      };

    case LOAD_BONUS_TIMELINE_FAILURE:
      return {
        ...state,
        loading: BONUS_TIMELINE_ERROR,
        error: action.payload,
      };

    case RESET_BONUS_TIMELINE:
      return initialState;

    default:
      return state;
  }
}

export function isBonusTimelineLoaded(globalState: StoreState): boolean {
  return (
    globalState.bonusTimeline &&
    (BONUS_TIMELINE_LOADED === globalState.bonusTimeline.loading ||
      BONUS_TIMELINE_ERROR === globalState.bonusTimeline.loading)
  );
}

export function isBonusTimelineEmpty(globalState: StoreState): boolean {
  return (
    globalState.bonusTimeline.bonus.rejected.length === 0 &&
    globalState.bonusTimeline.bonus.timeline.length === 0
  );
}

export function isBonusTimelineLoading(globalState: StoreState): boolean {
  return globalState.bonusTimeline && BONUS_TIMELINE_LOADING === globalState.bonusTimeline.loading;
}

export function isBonusTimelineStateUnknown(globalState: StoreState): boolean {
  return globalState.bonusTimeline && BONUS_TIMELINE_UNKNOWN === globalState.bonusTimeline.loading;
}

export const loadBonusTimeline = (refresh = false, userId = '') =>
  createAction({
    endpoint: endpointWithUserId('/timeline/bonus', userId),
    types: [LOAD_BONUS_TIMELINE, LOAD_BONUS_TIMELINE_SUCCESS, LOAD_BONUS_TIMELINE_FAILURE],
    method: HTTP_GET,
    bailout: (globalState: StoreState) => {
      return (
        (!refresh && isBonusTimelineLoaded(globalState)) || isBonusTimelineLoading(globalState)
      );
    },
  });

export const resetBonusTimeline = (): Action => {
  return {
    type: RESET_BONUS_TIMELINE,
  };
};

export const getFilteredTimeline = (
  timeline: BonusTimeline,
  filter: FilterType,
  filteredYears: number[],
) => {
  type BonusTimelineItemWithYear = BonusTimelineItem & { year?: number };
  const rejectedItems: BonusTimelineItemWithYear[] = timeline.rejected.map((activity) => ({
    type: BONUS_TIMELINE_PAYLOAD_ACTIVITY_TYPE,
    payload: activity,
    year: activity.year,
  }));

  const timelineItems: BonusTimelineItemWithYear[] = timeline.timeline.map((value) => {
    let year;
    if (value.type === BONUS_TIMELINE_PAYLOAD_ACTIVITY_TYPE) {
      year = value.payload.year;
    } else if (value.type !== BONUS_TIMELINE_PAYLOAD_MESSAGE_TYPE) {
      year = parseInt(value.payload.submissionDate.slice(0, 4), 10);
    }

    return {
      ...value,
      year,
    };
  });

  const allItems = rejectedItems.concat(timelineItems);

  if (!filter && (!filteredYears || filteredYears.length === 0)) {
    return allItems;
  }

  const combinedTimeline = allItems.filter(
    (value) => value.type !== BONUS_TIMELINE_PAYLOAD_MESSAGE_TYPE,
  ) as Exclude<BonusTimelineItem & { year: number }, MessageItem>[];

  const result = combinedTimeline.filter((value) => {
    switch (filter) {
      case FilterType.Activity:
        return value.type === BONUS_TIMELINE_PAYLOAD_ACTIVITY_TYPE;
      case FilterType.Reward:
        return (
          value.type === BONUS_TIMELINE_PAYLOAD_ORDER_ITEM_TYPE ||
          value.type === BONUS_TIMELINE_PAYLOAD_GIFT_ITEM_TYPE
        );
      default:
        return true;
    }
  });

  return result.filter((value) =>
    filteredYears.length > 0 ? filteredYears.includes(value.year) : true,
  );
};
